import { useState, useMemo } from 'react'
import {
  OwnershipRecord,
  ValidationError,
  PendingChange,
} from '../types/ownership'
import { INITIAL_OWNERSHIP_RECORDS } from '../data/mockData'

export function useOwnershipData() {
  const [records, setRecords] = useState<OwnershipRecord[]>(
    INITIAL_OWNERSHIP_RECORDS,
  )
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([])
  const [showHistorical, setShowHistorical] = useState(false)

  // Compute preview records by applying pending changes to base records
  const previewRecords = useMemo(() => {
    let current = [...records]

    // 1. Apply deletes first (remove from list)
    const deletedIds = new Set(
      pendingChanges.filter((c) => c.type === 'delete').map((c) => c.record.id),
    )

    // 2. Apply edits (replace existing)
    const edits = new Map(
      pendingChanges
        .filter((c) => c.type === 'edit')
        .map((c) => [c.record.id, c.record]),
    )

    // 3. Apply adds (append)
    const adds = pendingChanges
      .filter((c) => c.type === 'add')
      .map((c) => c.record)

    // Filter out deleted, apply edits
    current = current
      .filter((r) => !deletedIds.has(r.id))
      .map((r) => (edits.has(r.id) ? edits.get(r.id)! : r))

    // Append new records
    return [...current, ...adds]
  }, [records, pendingChanges])

  // For display in table, we want to show ALL records including pending ones,
  // but with visual indicators. So we need a list that includes:
  // - Base records (unless deleted)
  // - Edited records (replacing base)
  // - Added records
  // - DELETED records (kept but marked)
  const displayRecords = useMemo(() => {
    let display = [...records]

    // Map of pending changes by record ID for quick lookup
    const changesById = new Map(pendingChanges.map((c) => [c.record.id, c]))

    // Process existing records
    display = display.map((r) => {
      const change = changesById.get(r.id)
      if (change?.type === 'edit') return change.record // Show edited version
      return r // Show original
    })

    // Add new records
    const newRecords = pendingChanges
      .filter((c) => c.type === 'add')
      .map((c) => c.record)

    return [...display, ...newRecords].sort((a, b) =>
      a.effectiveStartDate.localeCompare(b.effectiveStartDate),
    )
  }, [records, pendingChanges])

  // Filter records based on view preference
  const filteredDisplayRecords = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]

    return displayRecords.filter((record) => {
      if (showHistorical) return true

      // Check if this record is pending delete - if so, show it regardless of date logic so user sees it
      const isPendingDelete = pendingChanges.some(
        (c) => c.record.id === record.id && c.type === 'delete',
      )
      if (isPendingDelete) return true

      // Active logic: Start Date <= Today AND (No End Date OR End Date >= Today)
      const isActive =
        record.effectiveStartDate <= today &&
        (!record.effectiveEndDate || record.effectiveEndDate >= today)

      return isActive
    })
  }, [displayRecords, showHistorical, pendingChanges])

  // Calculate total ownership for ACTIVE records only (using preview state)
  const totalOwnership = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    return previewRecords
      .filter((r) => {
        const isActive =
          r.effectiveStartDate <= today &&
          (!r.effectiveEndDate || r.effectiveEndDate >= today)
        return isActive
      })
      .reduce((sum, r) => sum + r.ownershipPercentage, 0)
  }, [previewRecords])

  const primaryMemberCount = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    return previewRecords.filter((r) => {
      const isActive =
        r.effectiveStartDate <= today &&
        (!r.effectiveEndDate || r.effectiveEndDate >= today)
      return isActive && r.isPrimaryMember
    }).length
  }, [previewRecords])

  // Validation Logic - Runs against PREVIEW state
  const validateRecord = (
    record: Omit<OwnershipRecord, 'id' | 'createdDate'>,
    excludeId?: string,
  ): ValidationError[] => {
    const errors: ValidationError[] = []
    const today = new Date().toISOString().split('T')[0]

    const isActiveNow =
      record.effectiveStartDate <= today &&
      (!record.effectiveEndDate || record.effectiveEndDate >= today)

    if (isActiveNow) {
      // Check against PREVIEW records (excluding the one being validated)
      const otherActiveRecords = previewRecords.filter(
        (r) =>
          r.id !== excludeId &&
          r.effectiveStartDate <= today &&
          (!r.effectiveEndDate || r.effectiveEndDate >= today),
      )
      const currentTotal = otherActiveRecords.reduce(
        (sum, r) => sum + r.ownershipPercentage,
        0,
      )

      if (currentTotal + record.ownershipPercentage > 100) {
        errors.push({
          field: 'ownershipPercentage',
          message: `Total ownership cannot exceed 100%. Current total: ${currentTotal}%`,
        })
      }
    }

    // Date Overlap Validation for SAME Member
    const memberRecords = previewRecords.filter(
      (r) => r.memberId === record.memberId && r.id !== excludeId,
    )

    const hasOverlap = memberRecords.some((existing) => {
      const start1 = record.effectiveStartDate
      const end1 = record.effectiveEndDate || '9999-12-31'
      const start2 = existing.effectiveStartDate
      const end2 = existing.effectiveEndDate || '9999-12-31'

      return start1 <= end2 && start2 <= end1
    })

    if (hasOverlap) {
      errors.push({
        field: 'effectiveStartDate',
        message:
          'Effective dates cannot overlap with an existing record for this member.',
      })
    }

    // Basic Field Validation
    if (!record.effectiveStartDate) {
      errors.push({
        field: 'effectiveStartDate',
        message: 'Start Date is required',
      })
    }
    if (record.ownershipPercentage < 0 || record.ownershipPercentage > 100) {
      errors.push({
        field: 'ownershipPercentage',
        message: 'Percentage must be between 0 and 100',
      })
    }
    if (
      record.effectiveEndDate &&
      record.effectiveEndDate < record.effectiveStartDate
    ) {
      errors.push({
        field: 'effectiveEndDate',
        message: 'End Date cannot be before Start Date',
      })
    }

    return errors
  }

  const addRecord = (record: Omit<OwnershipRecord, 'id' | 'createdDate'>) => {
    // Validate against preview state
    const errors = validateRecord(record)
    if (errors.length > 0) return { success: false, errors }

    const newRecord: OwnershipRecord = {
      ...record,
      id: `own-new-${Date.now()}`, // Temporary ID
      createdDate: new Date().toISOString().split('T')[0],
    }

    const change: PendingChange = {
      id: `change-${Date.now()}`,
      type: 'add',
      record: newRecord,
      timestamp: Date.now(),
    }

    setPendingChanges((prev) => [...prev, change])
    return { success: true, errors: [] }
  }

  const updateRecord = (id: string, updates: Partial<OwnershipRecord>) => {
    // Find record in preview state
    const currentRecord = previewRecords.find((r) => r.id === id)
    // Or check if it's a pending add that hasn't been committed yet
    const pendingAdd = pendingChanges.find(
      (c) => c.type === 'add' && c.record.id === id,
    )

    const baseRecord = currentRecord || pendingAdd?.record

    if (!baseRecord)
      return {
        success: false,
        errors: [{ field: 'general', message: 'Record not found' }],
      }

    const mergedRecord = { ...baseRecord, ...updates }

    // Validate
    const errors = validateRecord(mergedRecord, id)
    if (errors.length > 0) return { success: false, errors }

    // If updating a pending ADD, just update the pending add change
    if (pendingAdd) {
      setPendingChanges((prev) =>
        prev.map((c) =>
          c.id === pendingAdd.id ? { ...c, record: mergedRecord } : c,
        ),
      )
    } else {
      // If updating an existing committed record, add/update EDIT change
      // Check if we already have a pending edit for this ID
      const existingEdit = pendingChanges.find(
        (c) => c.type === 'edit' && c.record.id === id,
      )

      if (existingEdit) {
        setPendingChanges((prev) =>
          prev.map((c) =>
            c.id === existingEdit.id ? { ...c, record: mergedRecord } : c,
          ),
        )
      } else {
        // New edit change
        const change: PendingChange = {
          id: `change-${Date.now()}`,
          type: 'edit',
          record: mergedRecord,
          originalRecord: baseRecord,
          timestamp: Date.now(),
        }
        setPendingChanges((prev) => [...prev, change])
      }
    }

    return { success: true, errors: [] }
  }

  const deleteRecord = (id: string) => {
    const record = previewRecords.find((r) => r.id === id)
    if (!record) return

    const today = new Date().toISOString().split('T')[0]

    // Deletion Rule: Only future-dated records can be deleted
    if (record.effectiveStartDate <= today) {
      return {
        success: false,
        error:
          'Cannot delete historical or active records. Only future-dated records can be deleted.',
      }
    }

    // Check if it's a pending ADD - if so, just remove the pending change
    const pendingAdd = pendingChanges.find(
      (c) => c.type === 'add' && c.record.id === id,
    )
    if (pendingAdd) {
      setPendingChanges((prev) => prev.filter((c) => c.id !== pendingAdd.id))
      return { success: true }
    }

    // Otherwise, add DELETE change
    // Check if already pending edit, remove it and replace with delete
    const change: PendingChange = {
      id: `change-${Date.now()}`,
      type: 'delete',
      record: record,
      timestamp: Date.now(),
    }

    setPendingChanges((prev) => [
      ...prev.filter((c) => c.record.id !== id), // Remove any pending edits for this ID
      change,
    ])

    return { success: true }
  }

  const applyPendingChanges = () => {
    setRecords(previewRecords)
    setPendingChanges([])
  }

  const discardPendingChanges = () => {
    setPendingChanges([])
  }

  return {
    records: filteredDisplayRecords, // For display (includes pending indicators)
    previewRecords, // For logic/calculations
    pendingChanges,
    hasPendingChanges: pendingChanges.length > 0,
    totalOwnership,
    primaryMemberCount,
    showHistorical,
    setShowHistorical,
    addRecord,
    updateRecord,
    deleteRecord,
    applyPendingChanges,
    discardPendingChanges,
  }
}
